import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../stylings/BucksWorkerOnlineOrderDetailsModal.css';

// FIXED: Move API endpoint outside component to prevent recreation
const STATUS_UPDATE_API_ENDPOINT = 'https://s9l70ztz3c.execute-api.us-east-1.amazonaws.com/default/bucks-admin-worker-online-order-status-update';
// NEW: Add inventory API endpoint for fetching product images
const INVENTORY_API_ENDPOINT = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-inventory-product-get';

function BucksWorkerOnlineOrderDetailsModal({ order, onClose, onOrderUpdate }) {
  const [currentOrder, setCurrentOrder] = useState(order);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Add these new state hooks for status updates
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusUpdateNotes, setStatusUpdateNotes] = useState('');

  // NEW: Add these new state hooks for refresh functionality
  const [isRefreshing, setIsRefreshing] = useState(false);

  // NEW: Add state for managing product images
  const [productImages, setProductImages] = useState({});
  const [loadingImages, setLoadingImages] = useState(false);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // NEW: Function to fetch product images for order items
  const fetchProductImages = useCallback(async () => {
    if (!currentOrder.items || currentOrder.items.length === 0) {
      return;
    }

    console.log('=== Fetching Product Images ===');
    console.log('Items to process:', currentOrder.items.length);

    setLoadingImages(true);
    const imageMap = {};

    try {
      // First, try to use existing image_url from order items
      for (const item of currentOrder.items) {
        const itemKey = item.id || item.name;
        
        // Check if item already has an image_url
        if (item.image_url) {
          console.log(`✅ Using existing image URL for ${item.name}:`, item.image_url);
          imageMap[itemKey] = item.image_url;
        }
      }

      // For items without images, try to find them by name in inventory
      const itemsNeedingImages = currentOrder.items.filter(item => 
        !imageMap[item.id || item.name] && (!item.product_id && !item.product_code)
      );

      if (itemsNeedingImages.length > 0) {
        console.log(`🔍 Searching inventory for ${itemsNeedingImages.length} items by name...`);
        
        // Search inventory for products by name
        const searchPromises = itemsNeedingImages.map(async (item) => {
          try {
            console.log(`🔍 Searching for: "${item.name}"`);
            
            // Search inventory API with the product name
            const response = await fetch(`${INVENTORY_API_ENDPOINT}?search=${encodeURIComponent(item.name)}&page_size=50`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            });

            if (response.ok) {
              const searchResult = await response.json();
              console.log(`Search results for "${item.name}":`, searchResult);
              
              if (searchResult.products && searchResult.products.length > 0) {
                // Find the best match - exact name match preferred
                let bestMatch = searchResult.products.find(p => 
                  p.name.toLowerCase() === item.name.toLowerCase()
                );
                
                // If no exact match, use the first result
                if (!bestMatch) {
                  bestMatch = searchResult.products[0];
                }
                
                console.log(`Best match for "${item.name}":`, bestMatch);
                
                // Get detailed product info with images
                if (bestMatch) {
                  const detailResponse = await fetch(`${INVENTORY_API_ENDPOINT}?product_id=${bestMatch.id}`, {
                    method: 'GET',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                  });

                  if (detailResponse.ok) {
                    const productData = await detailResponse.json();
                    console.log(`Product details for "${item.name}":`, productData);
                    
                    // Store the primary image URL if available
                    if (productData.images && productData.images.length > 0) {
                      const primaryImage = productData.images.find(img => img.is_primary) || productData.images[0];
                      if (primaryImage && primaryImage.image_url) {
                        const itemKey = item.id || item.name;
                        imageMap[itemKey] = primaryImage.image_url;
                        console.log(`✅ Found image for "${item.name}":`, primaryImage.image_url);
                      }
                    }
                  }
                }
              } else {
                console.log(`❌ No products found for "${item.name}"`);
              }
            } else {
              console.warn(`❌ Search failed for "${item.name}":`, response.status);
            }
          } catch (error) {
            console.error(`❌ Error searching for "${item.name}":`, error);
          }
        });

        await Promise.all(searchPromises);
      }

      console.log('=== FINAL RESULTS ===');
      console.log('Final image map:', imageMap);
      console.log('Images found:', Object.keys(imageMap).length);
      console.log('=====================');
      
      setProductImages(imageMap);
    } catch (error) {
      console.error('Error fetching product images:', error);
    } finally {
      setLoadingImages(false);
    }
  }, [currentOrder.items]);

  // NEW: Fetch product images when component mounts or items change
  useEffect(() => {
    if (currentOrder.items && currentOrder.items.length > 0) {
      fetchProductImages();
    }
  }, [fetchProductImages]);

  // NEW: Add refresh handler
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      if (onOrderUpdate) {
        await onOrderUpdate();
      }
      // Refetch product images after order update
      await fetchProductImages();
      // Add a small delay to show the refresh animation
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    } catch (error) {
      console.error('Refresh failed:', error);
      setIsRefreshing(false);
    }
  }, [onOrderUpdate, fetchProductImages]);

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (err) {
      return 'Invalid date';
    }
  };

  // Update getStatusBadgeClass function
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bucks-modal-status-pending';
      case 'processing':
        return 'bucks-modal-status-processing';
      case 'prepared':
        return 'bucks-modal-status-prepared';
      case 'shipped':
      case 'out for delivery':
      case 'ready for pickup':
        return 'bucks-modal-status-shipped';
      case 'delivered':
      case 'picked up':
        return 'bucks-modal-status-delivered';
      case 'cancelled':
        return 'bucks-modal-status-cancelled';
      case 'refunded':
        return 'bucks-modal-status-refunded';
      default:
        return 'bucks-modal-status-pending';
    }
  };

  // FIXED: Memoized status normalization to prevent excessive calls
  const normalizeOrderStatus = useCallback((order) => {
    // Only log if in development mode to reduce console spam
    if (process.env.NODE_ENV === 'development') {
      console.log('=== Frontend Status Normalization ===');
      console.log('Order object:', order);
      console.log('Available status fields:', {
        original_status: order.original_status,
        status: order.status,
        order_status: order.order_status
      });
    }

    // Get the raw status from all possible fields, prioritizing original_status
    const possibleStatuses = [
      order.original_status,
      order.status, 
      order.order_status
    ].filter(Boolean);

    if (process.env.NODE_ENV === 'development') {
      console.log('Possible statuses (filtered):', possibleStatuses);
    }

    if (possibleStatuses.length === 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('No status found, defaulting to Pending');
      }
      return 'Pending';
    }

    // Use the first non-null status
    let rawStatus = possibleStatuses[0];
    if (process.env.NODE_ENV === 'development') {
      console.log('Raw status selected:', rawStatus, 'Type:', typeof rawStatus);
    }
    
    // FIXED: More robust string cleaning
    let status = String(rawStatus)
      .trim()                                    // Remove leading/trailing whitespace
      .replace(/[\u00A0]/g, ' ')                // Convert non-breaking spaces to regular spaces
      .replace(/[\u200B-\u200D\uFEFF]/g, '')    // Remove zero-width characters
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Remove control characters
      .replace(/\s+/g, ' ')                     // Replace multiple spaces with single space
      .trim();                                  // Trim again after cleanup

    if (process.env.NODE_ENV === 'development') {
      console.log('Cleaned status:', status);
    }

    // Check for exact matches with valid statuses
    const VALID_STATUSES = [
      'Pending',
      'Processing',
      'Prepared',
      'Out for Delivery',
      'Delivered',
      'Ready for Pickup',
      'Picked Up',
      'Cancelled',
      'Refunded'
    ];

    // Test exact matches first
    for (const validStatus of VALID_STATUSES) {
      if (status === validStatus) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Exact match found:', validStatus);
        }
        return validStatus;
      }
    }

    // Fallback to case-insensitive comparison
    const lowerStatus = status.toLowerCase();
    const statusMap = {
      'pending': 'Pending',
      'processing': 'Processing',
      'prepared': 'Prepared',
      'out for delivery': 'Out for Delivery',
      'delivered': 'Delivered',
      'ready for pickup': 'Ready for Pickup',
      'picked up': 'Picked Up',
      'cancelled': 'Cancelled',
      'canceled': 'Cancelled',
      'refunded': 'Refunded',
      'shipped': 'Out for Delivery'  // Handle backend mapping
    };

    if (statusMap[lowerStatus]) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Case-insensitive match found:', statusMap[lowerStatus]);
      }
      return statusMap[lowerStatus];
    }

    // Return cleaned status as fallback
    if (process.env.NODE_ENV === 'development') {
      console.log('No match found, returning cleaned status:', status);
      console.log('====================================');
    }
    return status;
  }, []); // Empty dependencies since function doesn't depend on external values

  // FIXED: Use useMemo to cache the normalized status result
  const currentNormalizedStatus = useMemo(() => {
    return normalizeOrderStatus(currentOrder);
  }, [currentOrder, normalizeOrderStatus]);

  // FIXED: Use useMemo to cache the status text result
  const currentStatusText = useMemo(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Display status for order:', currentNormalizedStatus);
    }
    return currentNormalizedStatus;
  }, [currentNormalizedStatus]);

  // Update nextAvailableStatuses logic
  const nextAvailableStatuses = useMemo(() => {
    const deliveryMethod = currentOrder.delivery_method;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('=== Getting Next Available Statuses ===');
      console.log('Current status:', currentNormalizedStatus);
      console.log('Delivery method:', deliveryMethod);
    }
    
    let nextStatuses = [];
    
    // Determine next statuses based on current status and delivery method
    if (currentNormalizedStatus === 'Pending') {
      nextStatuses = ['Processing'];
    } else if (currentNormalizedStatus === 'Processing') {
      nextStatuses = ['Prepared'];
    } else if (currentNormalizedStatus === 'Prepared') {
      if (deliveryMethod === 'Delivery') {
        nextStatuses = ['Out for Delivery'];
      } else {
        nextStatuses = ['Ready for Pickup'];
      }
    } else if (currentNormalizedStatus === 'Out for Delivery') {
      nextStatuses = ['Delivered'];
    } else if (currentNormalizedStatus === 'Ready for Pickup') {
      nextStatuses = ['Picked Up'];
    } else if (currentNormalizedStatus === 'Delivered' || currentNormalizedStatus === 'Picked Up') {
      nextStatuses = []; // Final states
    } else if (currentNormalizedStatus === 'Cancelled' || currentNormalizedStatus === 'Refunded') {
      nextStatuses = []; // Final states
    }
    
    // Always allow cancellation unless already in a final state
    const finalStatuses = ['Cancelled', 'Delivered', 'Picked Up', 'Refunded'];
    if (!finalStatuses.includes(currentNormalizedStatus)) {
      if (!nextStatuses.includes('Cancelled')) {
        nextStatuses.push('Cancelled');
      }
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Next available statuses:', nextStatuses);
      console.log('======================================');
    }
    
    return nextStatuses;
  }, [currentNormalizedStatus, currentOrder.delivery_method]);

  // FIXED: Enhanced status update with better error handling and debugging
  const handleStatusUpdate = useCallback(async (newStatus) => {
    if (!newStatus) return;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('=== Frontend Status Update ===');
      console.log('Current order:', currentOrder);
      console.log('New status:', newStatus);
      console.log('Current normalized status:', currentNormalizedStatus);
    }
    
    setIsUpdatingStatus(true);
    
    try {
      // Extract numeric ID from order ID (handle ORD00123 format)
      let orderIdToUse = currentOrder.internal_id;
      if (!orderIdToUse) {
        const orderIdStr = currentOrder.id.toString();
        if (orderIdStr.startsWith('ORD') || orderIdStr.startsWith('#ORD')) {
          orderIdToUse = parseInt(orderIdStr.replace(/[^0-9]/g, ''));
        } else {
          orderIdToUse = parseInt(orderIdStr.replace(/[^0-9]/g, ''));
        }
      }
      
      const requestBody = {
        order_id: orderIdToUse,
        new_status: newStatus,
        worker_notes: statusUpdateNotes.trim()
      };
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Request body:', requestBody);
        console.log('API endpoint:', STATUS_UPDATE_API_ENDPOINT);
      }
      
      const response = await fetch(STATUS_UPDATE_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      }

      const result = await response.json();
      if (process.env.NODE_ENV === 'development') {
        console.log('Response body:', result);
      }

      if (!response.ok) {
        // Enhanced error logging for debugging
        console.error('Status update failed:', {
          status: response.status,
          statusText: response.statusText,
          result: result,
          requestBody: requestBody
        });
        
        let errorMessage = result.error || `HTTP ${response.status}: Failed to update status`;
        
        // Add debug info to error message if available
        if (result.debug_info) {
          console.error('Debug info from backend:', result.debug_info);
          errorMessage += `\n\nDebug Info:\n- Current status: "${result.debug_info.current_status}"\n- New status: "${result.debug_info.new_status}"\n- Delivery method: "${result.debug_info.delivery_method}"\n- Raw DB status: "${result.debug_info.raw_current_status}"`;
        }
        
        throw new Error(errorMessage);
      }

      if (result.success) {
        // Update handleOrderUpdate to handle prepared_at timestamp
        const updatedOrder = {
          ...currentOrder,
          status: result.data.new_status,
          original_status: result.data.new_status,
          prepared_at: result.data.prepared_at,
          out_for_delivery_at: result.data.out_for_delivery_at,
          delivered_at: result.data.delivered_at,
          ready_for_pickup_at: result.data.ready_for_pickup_at,
          picked_up_at: result.data.picked_up_at,
          admin_notes: result.data.admin_notes,
          updated_at: result.data.updated_at
        };
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Updated order:', updatedOrder);
        }
        setCurrentOrder(updatedOrder);
        setStatusUpdateNotes(''); // Clear notes after successful update
        
        // Show success message
        window.alert(`Status successfully updated to ${result.data.new_status}`);
        
        // Trigger a refresh of the parent component
        if (onOrderUpdate) {
          onOrderUpdate();
        }
      } else {
        throw new Error(result.error || 'Update failed');
      }
    } catch (error) {
      console.error('=== Status Update Error ===');
      console.error('Error details:', error);
      console.error('Error message:', error.message);
      console.error('========================');
      
      // Show detailed error message
      const errorMsg = error.message || 'Unknown error occurred';
      window.alert(`Failed to update status: ${errorMsg}`);
    } finally {
      setIsUpdatingStatus(false);
      if (process.env.NODE_ENV === 'development') {
        console.log('=== Status Update Complete ===');
      }
    }
  }, [currentOrder, currentNormalizedStatus, statusUpdateNotes, onOrderUpdate]);

  // Update statusTimeline to include Prepared step
  const statusTimeline = useMemo(() => {
    const timeline = [];

    // Order placed
    if (currentOrder.placed_at || currentOrder.created_at) {
      timeline.push({
        status: 'Order Placed',
        timestamp: currentOrder.placed_at || currentOrder.created_at,
        icon: '📝',
        completed: true
      });
    }

    // Processing
    const isProcessing = ['Processing', 'Prepared', 'Out for Delivery', 'Ready for Pickup', 'Delivered', 'Picked Up'].includes(currentNormalizedStatus);
    timeline.push({
      status: 'Processing',
      timestamp: null,
      icon: '⚙️',
      completed: isProcessing
    });

    // Prepared
    const isPrepared = ['Prepared', 'Out for Delivery', 'Ready for Pickup', 'Delivered', 'Picked Up'].includes(currentNormalizedStatus);
    timeline.push({
      status: 'Prepared',
      timestamp: currentOrder.prepared_at,
      icon: '📦',
      completed: !!currentOrder.prepared_at || isPrepared
    });

    // Delivery/Pickup specific steps
    if (currentOrder.delivery_method?.toLowerCase() === 'delivery') {
      timeline.push({
        status: 'Out for Delivery',
        timestamp: currentOrder.out_for_delivery_at,
        icon: '🚚',
        completed: !!currentOrder.out_for_delivery_at
      });

      timeline.push({
        status: 'Delivered',
        timestamp: currentOrder.delivered_at,
        icon: '✅',
        completed: !!currentOrder.delivered_at
      });
    } else {
      timeline.push({
        status: 'Ready for Pickup',
        timestamp: currentOrder.ready_for_pickup_at,
        icon: '🏪',
        completed: !!currentOrder.ready_for_pickup_at
      });

      timeline.push({
        status: 'Picked Up',
        timestamp: currentOrder.picked_up_at,
        icon: '✅',
        completed: !!currentOrder.picked_up_at
      });
    }

    return timeline;
  }, [currentOrder, currentNormalizedStatus]);

  // Handle background click
  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Calculate totals
  const subtotal = currentOrder.subtotal || 0;
  const tax = currentOrder.tax || 0;
  const shipping = currentOrder.shipping || 0;
  const discount = currentOrder.discount_amount || 0;
  const total = currentOrder.total || 0;

  return (
    <div className="bucks-modal-overlay" onClick={handleBackgroundClick}>
      <div className="bucks-modal-container">
        {/* Modal Header */}
        <div className="bucks-modal-header">
          <div className="bucks-modal-title-section">
            <div className="bucks-modal-title-content">
              <h2 className="bucks-modal-title">Order Details</h2>
              <div className="bucks-modal-order-number">#{currentOrder.id}</div>
            </div>
            <div className={`bucks-modal-status-badge ${getStatusBadgeClass(currentOrder.status)}`}>
              {currentStatusText}
            </div>
          </div>
          <div className="bucks-modal-header-actions">
            <button 
              onClick={handleRefresh}
              className={`bucks-modal-refresh-btn ${isRefreshing ? 'bucks-refreshing' : ''}`}
              disabled={isRefreshing || isUpdatingStatus}
              title="Refresh order data"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12c0 5-4 9-9 9s-9-4-9-9 4-9 9-9c4.13 0 7.61 2.74 8.65 6.5"/>
                <polyline points="21,8 21,12 17,12"/>
              </svg>
            </button>
            <button onClick={onClose} className="bucks-modal-close-btn" title="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Navigation */}
        <div className="bucks-modal-navigation">
          <button
            className={`bucks-nav-tab ${activeTab === 'overview' ? 'bucks-nav-active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-4"/>
              <polyline points="6,9 12,15 18,9"/>
            </svg>
            <span>Overview</span>
          </button>
          <button
            className={`bucks-nav-tab ${activeTab === 'items' ? 'bucks-nav-active' : ''}`}
            onClick={() => setActiveTab('items')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 11V7a4 4 0 0 0-8 0v4H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-3z"/>
            </svg>
            <span>Items</span>
            {loadingImages && <div className="bucks-loading-badge">⏳</div>}
          </button>
          <button
            className={`bucks-nav-tab ${activeTab === 'timeline' ? 'bucks-nav-active' : ''}`}
            onClick={() => setActiveTab('timeline')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12,6 12,12 16,14"/>
            </svg>
            <span>Timeline</span>
          </button>
        </div>

        {/* Modal Content */}
        <div className="bucks-modal-content">
          {activeTab === 'overview' && (
            <div className="bucks-overview-tab">
              <div className="bucks-overview-grid">
                {/* Customer Information */}
                <div className="bucks-info-card">
                  <div className="bucks-card-header">
                    <h3>Customer Information</h3>
                    <div className="bucks-card-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                    </div>
                  </div>
                  <div className="bucks-customer-profile">
                    <div className="bucks-customer-avatar-large">
                      {currentOrder.customer?.name?.charAt(0)?.toUpperCase() || 'C'}
                    </div>
                    <div className="bucks-customer-info">
                      <h4>{currentOrder.customer?.name || 'Unknown Customer'}</h4>
                      <div className="bucks-contact-list">
                        {currentOrder.customer?.email && (
                          <div className="bucks-contact-item">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                              <polyline points="22,6 12,13 2,6"/>
                            </svg>
                            <a href={`mailto:${currentOrder.customer.email}`}>
                              {currentOrder.customer.email}
                            </a>
                          </div>
                        )}
                        {currentOrder.customer?.phone && (
                          <div className="bucks-contact-item">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                            </svg>
                            <a href={`tel:${currentOrder.customer.phone}`}>
                              {currentOrder.customer.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Information */}
                <div className="bucks-info-card">
                  <div className="bucks-card-header">
                    <h3>
                      {currentOrder.delivery_method === 'Delivery' ? 'Delivery Information' : 'Pickup Information'}
                    </h3>
                    <div className="bucks-card-icon">
                      {currentOrder.delivery_method?.toLowerCase() === 'delivery' ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 3h15v13H1z"/>
                          <path d="M16 8h4l3 3v5h-7V8z"/>
                          <circle cx="5.5" cy="18.5" r="2.5"/>
                          <circle cx="18.5" cy="18.5" r="2.5"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                          <circle cx="12" cy="10" r="3"/>
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="bucks-delivery-info">
                    <div className="bucks-delivery-method-display">
                      <span className="bucks-method-badge">
                        {currentOrder.delivery_method || 'Pickup'}
                      </span>
                    </div>
                    {currentOrder.delivery_method?.toLowerCase() === 'delivery' && currentOrder.shipping_address && (
                      <div className="bucks-address-section">
                        <h4>Delivery Address</h4>
                        <div className="bucks-address-details">
                          {currentOrder.shipping_address.street && (
                            <div>{currentOrder.shipping_address.street}</div>
                          )}
                          <div>
                            {[
                              currentOrder.shipping_address.city,
                              currentOrder.shipping_address.state,
                              currentOrder.shipping_address.zipcode
                            ].filter(Boolean).join(', ')}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Summary */}
                <div className="bucks-info-card">
                  <div className="bucks-card-header">
                    <h3>Order Summary</h3>
                    <div className="bucks-card-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-4"/>
                        <polyline points="6,9 12,15 18,9"/>
                      </svg>
                    </div>
                  </div>
                  <div className="bucks-order-summary">
                    <div className="bucks-summary-row">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(subtotal)}</span>
                    </div>
                    {discount > 0 && (
                      <div className="bucks-summary-row bucks-discount-row">
                        <span>
                          Discount
                          {currentOrder.promo_code && ` (${currentOrder.promo_code})`}:
                        </span>
                        <span>-{formatCurrency(discount)}</span>
                      </div>
                    )}
                    {tax > 0 && (
                      <div className="bucks-summary-row">
                        <span>Tax:</span>
                        <span>{formatCurrency(tax)}</span>
                      </div>
                    )}
                    {shipping > 0 && (
                      <div className="bucks-summary-row">
                        <span>Delivery Fee:</span>
                        <span>{formatCurrency(shipping)}</span>
                      </div>
                    )}
                    <div className="bucks-summary-row bucks-total-row">
                      <span>Total:</span>
                      <span>{formatCurrency(total)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Information */}
                <div className="bucks-info-card">
                  <div className="bucks-card-header">
                    <h3>Payment Information</h3>
                    <div className="bucks-card-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                        <line x1="1" y1="10" x2="23" y2="10"/>
                      </svg>
                    </div>
                  </div>
                  <div className="bucks-payment-details">
                    <div className="bucks-payment-item">
                      <span className="bucks-payment-label">Method:</span>
                      <span className="bucks-payment-value">{currentOrder.payment_method || 'Cash/Other'}</span>
                    </div>
                    <div className="bucks-payment-item">
                      <span className="bucks-payment-label">Status:</span>
                      <span className={`bucks-payment-status-badge ${currentOrder.payment_status?.toLowerCase()}`}>
                        {currentOrder.payment_status || 'Unpaid'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {(currentOrder.notes || currentOrder.admin_notes) && (
                  <div className="bucks-info-card bucks-notes-card">
                    <div className="bucks-card-header">
                      <h3>Notes</h3>
                      <div className="bucks-card-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14,2 14,8 20,8"/>
                          <line x1="16" y1="13" x2="8" y2="13"/>
                          <line x1="16" y1="17" x2="8" y2="17"/>
                          <polyline points="10,9 9,9 8,9"/>
                        </svg>
                      </div>
                    </div>
                    <div className="bucks-notes-content">
                      {currentOrder.notes && (
                        <div className="bucks-note-item">
                          <h4>Customer Instructions:</h4>
                          <p>{currentOrder.notes}</p>
                        </div>
                      )}
                      {currentOrder.admin_notes && (
                        <div className="bucks-note-item">
                          <h4>Admin Notes:</h4>
                          <p>{currentOrder.admin_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'items' && (
            <div className="bucks-items-tab">
              {loadingImages && (
                <div className="bucks-loading-message">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12c0 5-4 9-9 9s-9-4-9-9 4-9 9-9c4.13 0 7.61 2.74 8.65 6.5"/>
                    <polyline points="21,8 21,12 17,12"/>
                  </svg>
                  <span>Loading product images...</span>
                </div>
              )}
              <div className="bucks-items-grid">
                {currentOrder.items && currentOrder.items.length > 0 ? (
                  currentOrder.items.map((item, index) => {
                    // Get the image URL from our fetched data
                    const imageUrl = productImages[item.id || item.name] || item.image_url;
                    
                    return (
                      <div key={item.id || index} className="bucks-item-card">
                        <div className="bucks-item-image-container">
                          {imageUrl ? (
                            <img 
                              src={imageUrl} 
                              alt={item.name}
                              className="bucks-item-image"
                              onError={(e) => {
                                console.error(`Failed to load image for ${item.name}:`, imageUrl);
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className="bucks-item-placeholder"
                            style={{ display: imageUrl ? 'none' : 'flex' }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                              <circle cx="8.5" cy="8.5" r="1.5"/>
                              <polyline points="21,15 16,10 5,21"/>
                            </svg>
                          </div>
                        </div>
                        <div className="bucks-item-details">
                          <h4 className="bucks-item-name">{item.name}</h4>
                          {item.description && (
                            <p className="bucks-item-description">{item.description}</p>
                          )}
                          <div className="bucks-item-meta">
                            {item.category && (
                              <span className="bucks-item-tag">{item.category}</span>
                            )}
                            {item.product_code && (
                              <span className="bucks-item-tag">#{item.product_code}</span>
                            )}
                            {item.product_id && (
                              <span className="bucks-item-tag">ID: {item.product_id}</span>
                            )}
                          </div>
                          <div className="bucks-item-pricing">
                            <div className="bucks-pricing-row">
                              <span className="bucks-pricing-label">Quantity:</span>
                              <span className="bucks-pricing-value">{item.quantity}</span>
                            </div>
                            <div className="bucks-pricing-row">
                              <span className="bucks-pricing-label">Price Each:</span>
                              <span className="bucks-pricing-value">{formatCurrency(item.price)}</span>
                            </div>
                            <div className="bucks-pricing-row bucks-total-pricing">
                              <span className="bucks-pricing-label">Item Total:</span>
                              <span className="bucks-pricing-value">{formatCurrency(item.total)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="bucks-empty-items">
                    <div className="bucks-empty-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M16 11V7a4 4 0 0 0-8 0v4H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-3z"/>
                      </svg>
                    </div>
                    <h3>No Items Found</h3>
                    <p>This order doesn't contain any items.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="bucks-timeline-tab">
              <div className="bucks-timeline-grid">
                {/* Order Progress Card */}
                <div className="bucks-info-card bucks-timeline-progress-card">
                  <div className="bucks-card-header">
                    <h3>Order Progress</h3>
                    <div className="bucks-card-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12,6 12,12 16,14"/>
                      </svg>
                    </div>
                  </div>
                  
                  {/* Compact Horizontal Timeline */}
                  <div className="bucks-progress-tracker">
                    {statusTimeline.map((step, index) => (
                      <div key={index} className={`bucks-progress-step ${step.completed ? 'bucks-step-completed' : 'bucks-step-pending'}`}>
                        <div className="bucks-step-indicator">
                          <div className="bucks-step-icon">
                            {step.completed ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20,6 9,17 4,12"/>
                              </svg>
                            ) : (
                              <span className="bucks-step-number">{index + 1}</span>
                            )}
                          </div>
                          {index < statusTimeline.length - 1 && (
                            <div className={`bucks-step-connector ${step.completed ? 'bucks-connector-completed' : 'bucks-connector-pending'}`}></div>
                          )}
                        </div>
                        <div className="bucks-step-content">
                          <div className="bucks-step-title">{step.status}</div>
                          <div className="bucks-step-time">
                            {step.timestamp ? formatDateTime(step.timestamp) : 'Pending'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Current Status Summary */}
                  <div className="bucks-current-progress">
                    <div className="bucks-progress-summary">
                      <span className="bucks-progress-label">Current Status:</span>
                      <span className={`bucks-modal-status-badge ${getStatusBadgeClass(currentOrder.status)}`}>
                        {currentStatusText}
                      </span>
                    </div>
                    {currentOrder.delivery_method && (
                      <div className="bucks-delivery-method-info">
                        <span className="bucks-method-icon">
                          {currentOrder.delivery_method?.toLowerCase() === 'delivery' ? '🚚' : '🏪'}
                        </span>
                        <span>{currentOrder.delivery_method}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Update Card - Only show if updates are available */}
                {nextAvailableStatuses.length > 0 && (
                  <div className="bucks-info-card bucks-status-update-card">
                    <div className="bucks-card-header">
                      <h3>Update Status</h3>
                      <div className="bucks-card-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 12l6-6 6 6"/>
                          <path d="M3 18l6-6 6 6"/>
                        </svg>
                      </div>
                    </div>
                    
                    <div className="bucks-quick-update-form">
                      <div className="bucks-update-options">
                        <label className="bucks-update-label">Next Actions:</label>
                        <div className="bucks-update-buttons-grid">
                          {nextAvailableStatuses.map((status) => (
                            <button
                              key={status}
                              className={`bucks-quick-update-btn bucks-update-${status.toLowerCase().replace(/\s+/g, '-')}`}
                              onClick={() => handleStatusUpdate(status)}
                              disabled={isUpdatingStatus}
                            >
                              {isUpdatingStatus ? (
                                <svg className="bucks-updating-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 12c0 5-4 9-9 9s-9-4-9-9 4-9 9-9c4.13 0 7.61 2.74 8.65 6.5"/>
                                  <polyline points="21,8 21,12 17,12"/>
                                </svg>
                              ) : (
                                <>
                                  <span className="bucks-btn-icon">
                                    {status === 'Processing' && '⚙️'}
                                    {status === 'Prepared' && '📦'}
                                    {status === 'Out for Delivery' && '🚚'}
                                    {status === 'Ready for Pickup' && '🏪'}
                                    {status === 'Delivered' && '✅'}
                                    {status === 'Picked Up' && '✅'}
                                    {status === 'Cancelled' && '❌'}
                                  </span>
                                  <span className="bucks-btn-text">{status}</span>
                                </>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bucks-compact-notes">
                        <label htmlFor="quick-notes" className="bucks-notes-label">
                          Add Notes:
                        </label>
                        <textarea
                          id="quick-notes"
                          value={statusUpdateNotes}
                          onChange={(e) => setStatusUpdateNotes(e.target.value)}
                          placeholder="Optional notes for this update..."
                          className="bucks-compact-textarea"
                          rows={2}
                          disabled={isUpdatingStatus}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Order Timeline Details Card */}
                <div className="bucks-info-card bucks-timeline-details-card">
                  <div className="bucks-card-header">
                    <h3>Timeline Details</h3>
                    <div className="bucks-card-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14,2 14,8 20,8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                      </svg>
                    </div>
                  </div>
                  
                  <div className="bucks-timeline-details">
                    <div className="bucks-detail-row">
                      <span className="bucks-detail-label">Order Placed:</span>
                      <span className="bucks-detail-value">{formatDateTime(currentOrder.created_at)}</span>
                    </div>
                    
                    {currentOrder.prepared_at && (
                      <div className="bucks-detail-row">
                        <span className="bucks-detail-label">Prepared:</span>
                        <span className="bucks-detail-value">{formatDateTime(currentOrder.prepared_at)}</span>
                      </div>
                    )}
                    
                    {currentOrder.out_for_delivery_at && (
                      <div className="bucks-detail-row">
                        <span className="bucks-detail-label">Out for Delivery:</span>
                        <span className="bucks-detail-value">{formatDateTime(currentOrder.out_for_delivery_at)}</span>
                      </div>
                    )}
                    
                    {currentOrder.ready_for_pickup_at && (
                      <div className="bucks-detail-row">
                        <span className="bucks-detail-label">Ready for Pickup:</span>
                        <span className="bucks-detail-value">{formatDateTime(currentOrder.ready_for_pickup_at)}</span>
                      </div>
                    )}
                    
                    {currentOrder.delivered_at && (
                      <div className="bucks-detail-row">
                        <span className="bucks-detail-label">Delivered:</span>
                        <span className="bucks-detail-value">{formatDateTime(currentOrder.delivered_at)}</span>
                      </div>
                    )}
                    
                    {currentOrder.picked_up_at && (
                      <div className="bucks-detail-row">
                        <span className="bucks-detail-label">Picked Up:</span>
                        <span className="bucks-detail-value">{formatDateTime(currentOrder.picked_up_at)}</span>
                      </div>
                    )}
                    
                    {currentOrder.updated_at && currentOrder.updated_at !== currentOrder.created_at && (
                      <div className="bucks-detail-row bucks-last-updated">
                        <span className="bucks-detail-label">Last Updated:</span>
                        <span className="bucks-detail-value">{formatDateTime(currentOrder.updated_at)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* No Updates Available Card - Only show when no updates possible */}
                {nextAvailableStatuses.length === 0 && (
                  <div className="bucks-info-card bucks-final-status-card">
                    <div className="bucks-card-header">
                      <h3>Order Complete</h3>
                      <div className="bucks-card-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                          <polyline points="22,4 12,14.01 9,11.01"/>
                        </svg>
                      </div>
                    </div>
                    
                    <div className="bucks-final-status-content">
                      <div className="bucks-completion-message">
                        <span className="bucks-completion-icon">
                          {currentNormalizedStatus === 'Delivered' && '🎉'}
                          {currentNormalizedStatus === 'Picked Up' && '🎉'}
                          {currentNormalizedStatus === 'Cancelled' && '❌'}
                          {currentNormalizedStatus === 'Refunded' && '💰'}
                        </span>
                        <div className="bucks-completion-text">
                          <h4>
                            {currentNormalizedStatus === 'Delivered' && 'Order Successfully Delivered!'}
                            {currentNormalizedStatus === 'Picked Up' && 'Order Successfully Picked Up!'}
                            {currentNormalizedStatus === 'Cancelled' && 'Order Cancelled'}
                            {currentNormalizedStatus === 'Refunded' && 'Order Refunded'}
                          </h4>
                          <p>This order has reached its final status and no further updates are needed.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="bucks-modal-footer">
          <div className="bucks-order-timestamps">
            <div className="bucks-timestamp-item">
              <span className="bucks-timestamp-label">Order Placed:</span>
              <span className="bucks-timestamp-value">{formatDateTime(currentOrder.created_at)}</span>
            </div>
            {currentOrder.updated_at && currentOrder.updated_at !== currentOrder.created_at && (
              <div className="bucks-timestamp-item">
                <span className="bucks-timestamp-label">Last Updated:</span>
                <span className="bucks-timestamp-value">{formatDateTime(currentOrder.updated_at)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BucksWorkerOnlineOrderDetailsModal;